"use client"
import { useMemo } from "react"
import { motion, AnimatePresence } from "motion/react"
import { capabilityMilestones } from "../extrapolated-graph/capabilityThresholds"

// A deliberately simple, self-contained capability chart for the collapsed
// mobile bubble. The whole revealed trajectory is always shown — the x-domain
// runs from the first data point to the current date, mapped by DATE (each
// segment's width is proportional to its time span). As the story advances,
// the domain grows so every segment squishes proportionally to make room for
// the newly revealed portion on the right; the current point stays pinned at
// the right edge. Everything is derived directly from currentDate (fed the
// HUD's 420ms-smoothed date by the caller), so the squish is continuous and
// coordinated — no per-segment animation, no index-based redistribution.

export type CapabilityPoint = { date: Date; value: number }

type MobileCapabilityLineChartProps = {
  // Smoothed story date; drives the whole reveal/squish directly.
  currentDate: Date
  // Full capability line (shared history + the active plan's future).
  points: CapabilityPoint[]
  width: number
  height: number
  padding?: { top: number; right: number; bottom: number; left: number }
  strokeWidth?: number
  lineColor?: string
  textColor?: string
  backgroundColor?: string
  showLabelOutline?: boolean
}

// Cubic smoothing between two points, from the AI-2027 chart. Exported for
// the compact-strip chart (MobileCompactHudStrip) so the two lines match.
export function curveSegment(a: [number, number], b: [number, number]) {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const slope = dx === 0 ? 0 : Math.abs(dy / dx)
  const scale = Math.min(0.25, 1 / (1 + slope))
  return ` C ${a[0] + dx * scale} ${a[1] + dy * (scale * 0.5)} ${
    b[0] - dx * scale
  } ${b[1] - dy * (scale * 0.5)} ${b[0]} ${b[1]}`
}

// capabilityMilestones lists some labels at several rungs; keep the first.
const uniqueThresholds = capabilityMilestones.filter(
  (threshold, index) =>
    capabilityMilestones.findIndex(other => other.label === threshold.label) === index,
)

export function MobileCapabilityLineChart({
  currentDate,
  points,
  width,
  height,
  padding = { top: 10, right: 12, bottom: 10, left: 8 },
  strokeWidth = 2.5,
  lineColor = "#14202e",
  textColor = "#14202e",
  backgroundColor = "#fffff8",
  showLabelOutline = true,
}: MobileCapabilityLineChartProps) {
  const innerWidth = width - padding.left - padding.right
  const innerHeight = height - padding.top - padding.bottom

  const firstT = points.length > 0 ? points[0].date.getTime() : 0
  // Right edge of the domain = the current date, but never before the first
  // point (guard the divide-by-zero at the very start of the story).
  const currentT = Math.max(currentDate.getTime(), firstT + 1)
  const domainSpan = currentT - firstT

  // Date-proportional x: first point at the left edge, current at the right,
  // everything in between placed by its share of [firstT, currentT].
  const xForTime = (time: number) =>
    padding.left + ((time - firstT) / domainSpan) * innerWidth

  const valueAt = (time: number) => {
    if (points.length === 0) return 1
    if (time <= firstT) return points[0].value
    const lastPoint = points[points.length - 1]
    if (time >= lastPoint.date.getTime()) return lastPoint.value
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i]
      const b = points[i + 1]
      const at = a.date.getTime()
      const bt = b.date.getTime()
      if (time >= at && time <= bt) {
        const ratio = bt === at ? 0 : (time - at) / (bt - at)
        return a.value + ratio * (b.value - a.value)
      }
    }
    return lastPoint.value
  }

  // The revealed polyline: every point up to the current date, plus an
  // interpolated tip exactly at the current date (so the line ends at the
  // right edge).
  const linePoints = useMemo<Array<{ time: number; value: number }>>(() => {
    const out: Array<{ time: number; value: number }> = []
    for (const point of points) {
      const t = point.date.getTime()
      if (t < currentT) out.push({ time: t, value: point.value })
    }
    if (out.length === 0) out.push({ time: firstT, value: points[0]?.value ?? 1 })
    if (currentT > out[out.length - 1].time) {
      out.push({ time: currentT, value: valueAt(currentT) })
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, currentT, firstT])

  // Range-fit y (log) to the visible values, so the trajectory fills the
  // bubble vertically and the exponential curve reads as a gentle climb.
  const { logMin, logSpan } = useMemo(() => {
    const values = linePoints.map(point => point.value)
    const lo = Math.log(Math.min(...values))
    const hi = Math.log(Math.max(...values))
    return { logMin: lo, logSpan: hi - lo || 1 }
  }, [linePoints])
  const yForValue = (value: number) =>
    padding.top + innerHeight - ((Math.log(value) - logMin) / logSpan) * innerHeight

  const linePath = useMemo(() => {
    if (linePoints.length === 0) return ""
    const screen = linePoints.map(
      point => [xForTime(point.time), yForValue(point.value)] as [number, number],
    )
    let d = `M ${screen[0][0]} ${screen[0][1]}`
    for (let i = 0; i < screen.length - 1; i++) d += curveSegment(screen[i], screen[i + 1])
    return d
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linePoints, logMin, logSpan, domainSpan])

  const tipX = xForTime(currentT)
  const tipY = yForValue(valueAt(currentT))

  // Milestone markers where the line crosses a threshold, positioned on the
  // squishing scale; shown once the story has reached them, with the AI-2027
  // right-to-left overlap filtering.
  const markers = useMemo(() => {
    const found: Array<{ label: string; x: number; y: number }> = []
    for (const threshold of uniqueThresholds) {
      for (let i = 0; i < points.length - 1; i++) {
        const a = points[i].value
        const b = points[i + 1].value
        if (a <= threshold.value && threshold.value <= b) {
          const at = points[i].date.getTime()
          const bt = points[i + 1].date.getTime()
          const ratio = b === a ? 0 : (threshold.value - a) / (b - a)
          const time = at + ratio * (bt - at)
          if (time <= currentT) {
            found.push({ label: threshold.label, x: xForTime(time), y: yForValue(threshold.value) })
          }
          break
        }
      }
    }
    const charWidth = 6
    const kept: typeof found = []
    for (const marker of [...found].sort((m1, m2) => m2.x - m1.x)) {
      const box = {
        left: marker.x - 5 - marker.label.length * charWidth,
        right: marker.x - 5,
        top: marker.y - 6,
        bottom: marker.y + 6,
      }
      if (box.left < padding.left - 10) continue
      const overlaps = kept.some(other => {
        const otherBox = {
          left: other.x - 5 - other.label.length * charWidth,
          right: other.x - 5,
          top: other.y - 6,
          bottom: other.y + 6,
        }
        return !(
          box.right < otherBox.left ||
          box.left > otherBox.right ||
          box.bottom < otherBox.top ||
          box.top > otherBox.bottom
        )
      })
      if (!overlaps) kept.push(marker)
    }
    return kept
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, currentT, domainSpan, logMin, logSpan, padding.left])

  return (
    <svg width={width} height={height} style={{ display: "block", overflow: "hidden" }}>
      {/* The revealed trajectory, drawn to the current point. It tracks the
          smoothed date directly — no animation needed; the date smoothing is
          the single motion source, so the squish stays coordinated. */}
      <path
        d={linePath}
        stroke={lineColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />

      {/* Current-point dot at the right edge. */}
      <circle cx={tipX} cy={tipY} r={3} fill={backgroundColor} stroke={lineColor} strokeWidth={1.6} />

      {/* Milestone labels, fading in as they are reached. */}
      <AnimatePresence>
        {markers.map(marker => (
          <motion.g
            key={`milestone-${marker.label}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <circle cx={marker.x} cy={marker.y} r={2} fill={backgroundColor} stroke={lineColor} strokeWidth={1.2} />
            <text
              x={marker.x - 6}
              y={marker.y - 6}
              fill={textColor}
              fontSize={11}
              textAnchor="end"
              stroke={backgroundColor}
              strokeWidth={showLabelOutline ? 3 : 0}
              paintOrder="stroke"
              className="font-mono font-bold"
              style={{ pointerEvents: "none" }}
            >
              {marker.label}
            </text>
          </motion.g>
        ))}
      </AnimatePresence>
    </svg>
  )
}
